import { MessageView, Modal, RealTimeWrapper } from "components";
import {
  useAppLocation,
  useAppSearch,
  useCurrentUser,
} from "_graphql/cache/auth";
import { useCallback, useEffect } from "react";

import config from "config";
import {
  markClaimMessageRead,
  postClaimMessage,
} from "_graphql/visal/mutations/publish-offer";
import toast from "react-hot-toast";
import { useFormik } from "formik";
import { NetworkStatus, useMutation } from "@apollo/client";
import { useClaimParticipationThread } from "hooks/data/use-participation-thread";
import {
  PostClaimMessage,
  PostClaimMessageVariables,
} from "_graphql/visal/mutations/__generated__/PostClaimMessage";
import {
  MarkClaimMessageRead,
  MarkClaimMessageReadVariables,
} from "_graphql/visal/mutations/__generated__/MarkClaimMessageRead";
import { useOfferClaimShare } from "hooks/data/use-offer-claims";

export default function ClaimTransactionThreadContainer({
  open,
  setOpen,
  hideInput,
}: {
  open: boolean;
  setOpen: (val: boolean) => void;
  refetch?: () => void;
  hideInput?: boolean;
}) {
  const currentUser = useCurrentUser();
  const searchParams = useAppSearch();
  const { params } = useAppLocation();
  const { employee } = useCurrentUser();
  const participantId = searchParams?.id || "";
  const [sendMessage, { loading: sendingMessage }] = useMutation<
    PostClaimMessage,
    PostClaimMessageVariables
  >(postClaimMessage, {
    refetchQueries: ["GetClaimShareThread"],
  });
  const { share } = useOfferClaimShare({
    offerClaimParticipantId: searchParams?.id || "",
  });
  const [markMessagesAsRead] = useMutation<
    MarkClaimMessageRead,
    MarkClaimMessageReadVariables
  >(markClaimMessageRead, {
    refetchQueries: ["GetClaimShares"],
  });
  const {
    thread,
    loading,
    refetch: refetchThread,
    networkStatus,
  } = useClaimParticipationThread({
    claimId: params?.claim!,
    offerClaimParticipantId: participantId,
  });

  useEffect(() => {
    if (thread && thread?.length > 0) {
      markMessagesAsRead({
        variables: {
          messageId: thread
            ?.filter((el) => !el?.is_read)
            ?.map((el) => String(el?.claim_message_thread_id)),
          senderName:
            currentUser?.employee?.employee_first_name +
            " " +
            currentUser?.employee?.employee_last_name,
        },
      })
        .then((res) => {
          console.log("Messages marked as read");
        })
        .catch((err) => {
          toast.error("Error marking message as read", err?.message);
        });
    }
  }, [thread]);

  const handleSubmit = useCallback(
    async (values: { message: string }) => {
      try {
        sendMessage({
          variables: {
            body: values.message,
            claimId: String(params?.claim),
            claimParticipantId: String(searchParams?.id),
            isInternal: true,
            // subject: "He",
            reinsurerEmail:
              share?.offer_participant?.reinsurer?.re_company_email || "",
            source: "Visal Reinsurance",
            senderName:
              currentUser?.employee?.employee_first_name +
              " " +
              currentUser?.employee?.employee_last_name,
          },
        })
          .then(({ data }) => {
            if (data?.postClaimMessage) {
              toast.success("Message sent successfully");
              form.resetForm();
            } else {
              toast.error("Failed to send message");
            }
          })
          .catch((err) => {
            toast.error("Failed to send message", err?.message);
          });
      } catch (error) {
        toast.error("Failed to send message");
      }
    },
    [employee?.employee_first_name, employee?.employee_last_name]
  );

  const form = useFormik({
    initialValues: {
      message: "",
    },
    onSubmit: handleSubmit,
  });

  return (
    <Modal
      open={open}
      setOpen={setOpen}
      loading={loading && networkStatus === NetworkStatus.loading}
      size="3xl"
      title="Transaction Thread"
      hideActions={true}
      description="Communication thread between Reinsurance Underwriters on KAVA and PlaceIT"
      // renderActions={renderActions}
    >
      <div className="bg-blue-100 rounded-none mb-4 p-2">
        <p className="text-xs text-blue-800 px-4 pt-2">
          Secure Transactional Threads enable realtime, encrypted communication
          between communicating parties, ensuring confidential negotiations.
        </p>
        <p className="text-xs text-blue-800 px-4 pt-2">
          When both parties are online, they can collaborate instantly—sharing
          documents, discussing terms, and closing deals faster. Key benefits
          include end-to-end security, live interaction, and streamlined
          workflows for time-sensitive transactions.
        </p>
      </div>
      <RealTimeWrapper
        channel={config.pusher.channels.kava}
        events={[
          "reinsurer.interest",
          "reinsurer.remove_interest",
          "reinsurer.thread_notification",
        ]}
        callBack={() => refetchThread()}
      >
        <MessageView
          offers={thread?.map((el) => ({
            ...el,
            message: el?.body,
            sender_name: el?.sender_name,
            source: el?.is_internal ? "" : el?.source,
            timestamp: el?.created_at,
          }))}
          hideInput={hideInput}
          form={form}
          sending={sendingMessage}
        />
      </RealTimeWrapper>
    </Modal>
  );
}
